import React, { useState, useEffect, useCallback } from "react";
import { apiCall } from "../main/ApiCall";
import "../css/others/Popup.css";
import ToastPopup from "./ToastPopup";
import { EditCampaignPopupProps, createToastMessage, ToastMessage, ControlType } from '../interFaces/interfaces';
const formatDateForBackend = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};
const formatDateForInput = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const restrictedChars = /[\\:*?"<>|/]/;
const containsSpecialChars = (str: string) => {
  return restrictedChars.test(str);
};
const isWeekend = (dateValue: string) => {
  const date = new Date(dateValue);
  const day = date.getDay();
  return day === 0 || day === 6;
};
const EditCampaignPopup: React.FC<EditCampaignPopupProps> = ({ campaign, onClose }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [updatedCampaign, setUpdatedCampaign] = useState({ ...campaign, campaignStartDate: formatDateForInput(campaign.campaignStartDate), campaignEndDate: formatDateForInput(campaign.campaignEndDate) });
  const [reviewDuration, setReviewDuration] = useState<number>(0);
  const [minStartDate, setMinStartDate] = useState<string>("");
  const clientId = localStorage.getItem("clientId");
  const updatedBy = localStorage.getItem("email");
  const [auditScopeControls, setAuditScopeControls] = useState<string[]>([]);
  const [controls, setControls] = useState<ControlType[]>([]);
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [customText, setCustomText] = useState<string>("");
  const fetchClientConfig = useCallback(async () => {
    try {
      const response = await apiCall("get", `/api/client-config/${clientId}`);
      setReviewDuration(response.campaignQcReviewDuration || 0);
      if (response.campaignQcReviewDuration !== undefined) {
        const today = new Date();
        today.setDate(today.getDate() + response.campaignQcReviewDuration + 1);
        setMinStartDate(formatDateForInput(today.toString()));
      }
    } catch (error) {
      console.error("Failed to fetch client config", error);
    }
  }, [clientId]);
  useEffect(() => { fetchClientConfig() }, [fetchClientConfig]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (["auditObjective", "auditScope", "auditTeam"].includes(name)) {
      if (value.length > 60) {
        setMessage(createToastMessage("Maximum 60 characters allowed", "warning", 3000));
        return;
      }
      if (containsSpecialChars(value)) {
        setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
        return;
      }
    }
    if (name === "campaignStartDate") {
      if (isWeekend(value)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      const selectedDate = new Date(value);
      const minDate = new Date(minStartDate);
      if (selectedDate < minDate) {
        setMessage(createToastMessage(`Start date must be at least ${reviewDuration} days from today`, "error", 3000));
        return;
      }
      if (
        updatedCampaign.campaignEndDate &&
        new Date(updatedCampaign.campaignEndDate) < selectedDate
      ) {
        setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value, campaignEndDate: "" }));
      } else {
        setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
      }
    } else if (name === "campaignEndDate") {
      if (!updatedCampaign.campaignStartDate) {
        setMessage(createToastMessage("Please select a start date first", "warning", 3000));
        return;
      }
      const selectedDate = new Date(value);
      if (isWeekend(value)) {
        setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
        return;
      }
      if (new Date(value) < new Date(updatedCampaign.campaignStartDate)) {
        setMessage(createToastMessage("End date must be after start date", "warning", 3000));
        return;
      }
      if (selectedDate <= new Date(updatedCampaign.campaignStartDate)) {
        setMessage(createToastMessage("End date must be after start date", "warning", 3000));
        return;
      }
      setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
    } else {
      setUpdatedCampaign((prev: any) => ({ ...prev, [name]: value }));
    }
  };
  const handleSave = async () => {
    const textFields = ["auditObjective", "auditScope", "auditTeam"] as const;
    for (const field of textFields) {
      const fieldValue = updatedCampaign[field];
      if (typeof fieldValue === "string" && containsSpecialChars(fieldValue)) {
        const fieldLabel = field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        setMessage(createToastMessage(`Special characters are not allowed in ${fieldLabel}`, "warning", 3000));
        return;
      }
    }
    if (!updatedCampaign.campaignStartDate || !updatedCampaign.campaignEndDate) {
      setMessage(createToastMessage("Please select both start and end dates", "warning", 3000));
      return;
    }
    if (
      isWeekend(updatedCampaign.campaignStartDate) || isWeekend(updatedCampaign.campaignEndDate)
    ) {
      setMessage(createToastMessage("Saturday and Sunday are not allowed", "warning", 3000));
      return;
    }
    const startDate = new Date(updatedCampaign.campaignStartDate);
    const minStart = new Date(minStartDate);
    if (startDate < minStart) {
      setMessage(createToastMessage(`Start date must be at least ${reviewDuration} days from today`, "warning", 3000));
      return;
    }
    if (new Date(updatedCampaign.campaignEndDate) <= startDate) {
      setMessage(createToastMessage("End date must be after start date", "warning", 3000));
      return;
    }
    try {
      const updatedData: any = { campaignId: campaign.campaignId, auditObjective: updatedCampaign.auditObjective, auditScope: updatedCampaign.auditScope, auditTeam: updatedCampaign.auditTeam, campaignStartDate: formatDateForBackend(new Date(updatedCampaign.campaignStartDate)), campaignEndDate: formatDateForBackend(new Date(updatedCampaign.campaignEndDate)), updatedBy: updatedBy, updatedDate: formatDateForBackend(new Date()) };
      if (auditScopeControls.length > 1) {
        updatedData.auditScopeControls = auditScopeControls;
      }
      await apiCall("put", `/api/campaigns?campaignId=${campaign.campaignId}`, updatedData);
      setMessage(createToastMessage("Campaign updated successfully!", "success", 3000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Update failed.";
      setMessage(createToastMessage(errorMessage, "error", 3000));
      setTimeout(() => window.location.reload(), 3000);
    }
  };
  const getPrefix = (controls: string[]) => {
    if (controls.length === 1) {
      const firstWord = controls[0].split(" ")[0];
      return firstWord + "_";
    }
    return "";
  };
  useEffect(() => {
    const fetchControls = async () => {
      try {
        const response = await apiCall("get", "/api/enums/ControlsEnum");
        setControls(response);
      } catch (error) {
        console.error("Failed to fetch controls:", error);
      }
    };
    fetchControls();
  }, []);
  const handleControlToggle = (value: string) => {
    let updated;
    if (selectedControls.includes(value)) {
      updated = selectedControls.filter((c) => c !== value);
    } else {
      updated = [...selectedControls, value];
    }
    setSelectedControls(updated);
    setAuditScopeControls(updated);
    const prefix = getPrefix(updated);
    if (updated.length === 1) {
      let cleanText = customText;
      const allPrefixes = controls.map(c => c.value + "_");
      allPrefixes.forEach(p => {
        if (cleanText.startsWith(p)) {
          cleanText = cleanText.slice(p.length);
        }
      });
      const fullValue = prefix + cleanText;
      setCustomText(cleanText);
      setUpdatedCampaign((prev: any) => ({ ...prev, auditScope: fullValue }));
    }
    else if (updated.length > 1) {
      setCustomText("");
      setUpdatedCampaign((prev: any) => ({
        ...prev, auditScope: "",
      }));
    }
    else {
      setCustomText("");
      setUpdatedCampaign((prev: any) => ({ ...prev, auditScope: "" }));
    }
  };
  const handleScopeTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    const prefix = getPrefix(selectedControls);
    if (selectedControls.length === 1 && prefix && !value.startsWith(prefix)) {
      setMessage(createToastMessage("Prefix cannot be removed", "warning", 3000));
      return;
    }
    if (value.length <= 60) {
      const textWithoutPrefix = prefix ? value.slice(prefix.length) : value;
      let fullValue = "";
      if (selectedControls.length === 1) {
        fullValue = prefix + textWithoutPrefix;
        setCustomText(textWithoutPrefix);
      }
      else { fullValue = value; setCustomText(value) }
      setUpdatedCampaign((prev: any) => ({ ...prev, auditScope: fullValue }));
    }
  };
  useEffect(() => {
    if (campaign.auditScope) {
      setUpdatedCampaign((prev: any) => ({
        ...prev,
        auditScope: campaign.auditScope,
      }));
      const matchedControls = controls
        .map(c => c.value)
        .filter(val => {
          const firstWord = val.split(" ")[0];
          return campaign.auditScope.startsWith(firstWord + "_");
        });
      if (matchedControls.length === 1) {
        const prefix = matchedControls[0] + "_";
        setSelectedControls([matchedControls[0]]);
        setAuditScopeControls([matchedControls[0]]);
        if (campaign.auditScope.startsWith(prefix)) {
          setCustomText(campaign.auditScope.slice(prefix.length));
        } else {
          setCustomText(campaign.auditScope);
        }
        setUpdatedCampaign((prev: any) => ({
          ...prev, auditScope: campaign.auditScope,
        }));
      } else {
        setCustomText(campaign.auditScope);
      }
    }
  }, [campaign.auditScope, controls]);
  return (
    <div className="popup-overlay">
      <div className="modal-content1">
        <h3>Edit Campaign</h3>
        <div className="modal-content-list">
          <label>Audit Objective</label>
          <input type="text" name="auditObjective" value={updatedCampaign.auditObjective} onChange={handleChange} placeholder="Enter audit objective" />
          <label>Audit Team</label>
          <input type="text" name="auditTeam" value={updatedCampaign.auditTeam} onChange={handleChange} placeholder="Enter audit team" />
          <label>Campaign Start Date</label>
          <input type="date" name="campaignStartDate" value={updatedCampaign.campaignStartDate} min={minStartDate} onChange={handleChange} />
          <label>Campaign End Date</label>
          <input type="date" name="campaignEndDate" value={updatedCampaign.campaignEndDate} min={updatedCampaign.campaignStartDate || minStartDate} onChange={handleChange} />
          <label>Audit Scope</label>
          <input type="text" value={updatedCampaign.auditScope} onChange={handleScopeTextChange} placeholder="Enter scope..." disabled={selectedControls.length === 0} />
          <div className="audit-scope-container">
            <div className="audit-scope-controls">
              {controls.map((control) => (
                <label key={control.value} className="audit-scope-item">
                  <input type="checkbox" className="audit-scope-checkbox" checked={selectedControls.includes(control.value)} onChange={() => handleControlToggle(control.value)} />
                  <span className="audit-scope-text">
                    <strong>{control.value}</strong> ({control.description})
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}>Cancel</button>
          <button className="savebutton" onClick={handleSave}>Save</button>
        </div>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default EditCampaignPopup;